import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "fast-page-translation-wrapper" }
const _hoisted_2 = { class: "manipulation-area" }
const _hoisted_3 = { class: "button-area" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-Plus mr-1" }, null, -1)
const _hoisted_5 = { class: "search-area mr-1" }
const _hoisted_6 = { class: "filter-search input-group" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("div", { class: "input-group-text" }, [
  /*#__PURE__*/_createElementVNode("i", { class: "mdp-Filter" })
], -1)
const _hoisted_8 = [
  _hoisted_7
]
const _hoisted_9 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-Close" }, null, -1)
const _hoisted_10 = { class: "translation-table-wrapper mt-2" }
const _hoisted_11 = { class: "table translation-table" }
const _hoisted_12 = { class: "custom-table-tr" }
const _hoisted_13 = /*#__PURE__*/_createElementVNode("th", { class: "width-auto options-column" }, null, -1)
const _hoisted_14 = { class: "custom-table-header" }
const _hoisted_15 = { class: "custom-table-header-font my-2" }
const _hoisted_16 = {
  key: 0,
  class: "language-indication"
}
const _hoisted_17 = ["onClick"]
const _hoisted_18 = { class: "options-column width-auto" }
const _hoisted_19 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-Ellipsis" }, null, -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("span", { class: "menu-icon" }, [
  /*#__PURE__*/_createElementVNode("i", { class: "mdp-Delete" })
], -1)
const _hoisted_21 = { class: "menu-text" }
const _hoisted_22 = ["onUpdate:modelValue", "onInput"]

import { ILocalizedRecord } from "@medapro-types/common/components/localizedDataListEditorTypes";
import { ITranslationTableTypeModel } from "@medapro-types/Exports/components/FastPage/ITranslationTableTypeModel";
import { reactive, computed, nextTick, ref, watch } from "vue";
import exportApi from "@services/exports";
import { useI18n } from "vue-i18n";
import { MetaDataState } from "@medapro-types/common/metadata";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import TextBoxWithFallback from "@ContentEditor/components/TextBoxWithFallback.vue";
import ruleEvaluator from "@AttributeManager/validations/ruleEvaluator.js";
import _ from "lodash";

type ViewModel = {
  translations: ILocalizedRecord<ITranslationTableTypeModel>[];
  selectedRow: number | null;
  inputIsFocused: boolean;
  searchTerm: string;
  loading: boolean;
  dirty: boolean;
  rowsCount: number;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'TranslationTableDialog',
  props: {
    showTranslationTable: { type: Boolean }
  },
  emits: ["update:showTranslationTable"],
  setup(__props: any, { emit }) {

const props = __props;


const { t: $t } = useI18n();

const tableTranslation = ref<HTMLTableRowElement[]>([]);

//#region ViewModel
const vm = reactive<ViewModel>({
  translations: [] as ILocalizedRecord<ITranslationTableTypeModel>[],
  selectedRow: null,
  inputIsFocused: false,
  searchTerm: "",
  loading: false,
  dirty: false,
  rowsCount: 0
});

const _showTranslationTable = computed({
  get: () => {
    return props.showTranslationTable;
  },
  set: (nv) => {
    emit("update:showTranslationTable", nv);
  }
});

const closeModal = () => {
  emptySearchTerm();
  emit("update:showTranslationTable", false);
};

const sortedList = computed(() => {
  const filter = vm.searchTerm ? vm.searchTerm.toLowerCase() : null;
  return !filter ? vm.translations : vm.translations.filter((x) => x[primaryLanguage.value.languageCode].key.toLocaleLowerCase().includes(filter));
});

const emptySearchTerm = () => {
  vm.searchTerm = "";
  nextTick(() => {
    if (vm.rowsCount > vm.translations.length) {
      scrollToLastElement();
    }
  });
};

const selectRow = (index: number) => {
  if (vm.selectedRow != index) vm.selectedRow = index;
};

const deleteItem = (index: number) => {
  vm.translations.splice(index, 1);
};

const { useState: useMetaDataState } = createNamespacedHelpers<MetaDataState>("metadata");
const { contentLanguages } = useMetaDataState(["contentLanguages"]);

//#region Language
const languages = computed(() => {
  return contentLanguages.value.enabled;
});

const primaryLanguage = computed(() => {
  return contentLanguages.value.enabled[0];
});

const translatedLanguageCodes = computed((): string[] => {
  const translatedLangCodes = _.tail(contentLanguages.value.enabled);
  return translatedLangCodes.map((x) => x.languageCode);
});

//#endregion

async function loadTranslationTable() {
  try {
    vm.loading = true;
    const enabledLanguageCodes = contentLanguages.value.enabled.map((x) => x.languageCode);
    const data = await exportApi.getTranslationTable(enabledLanguageCodes);
    vm.translations = data;
    vm.rowsCount = vm.translations.length;
  } catch (error) {
    console.error("Error on loading translation table:", error);
  } finally {
    vm.loading = false;
  }
}

async function SaveTranslations() {
  try {
    const enabledLanguageCodes = contentLanguages.value.enabled.map((x) => x.languageCode);
    const strippedModels = vm.translations.map((x) => _.pick(x, enabledLanguageCodes));
    await exportApi.updateTranslationTable(strippedModels);
  } catch (error) {
    console.error(error);
  }
}

function addNew() {
  const row: ILocalizedRecord<ITranslationTableTypeModel> = Object.fromEntries(
    contentLanguages.value.enabled.map((x) => [
      x.languageCode,
      {
        key: generateNewName($t("newValues.TranslationKey"), vm.translations),
        value: "",
        languageName: x.languageCode
      }
    ])
  );

  vm.translations.push(row);

  if (vm.searchTerm) {
    vm.rowsCount = vm.translations.length + 1;
  }
  nextTick(() => {
    scrollToLastElement();
  });
}

async function scrollToLastElement() {
  const index = tableTranslation.value.length - 1;
  const tableRow = tableTranslation.value[index];
  selectRow(index);

  tableRow?.scrollIntoView({ behavior: "smooth" });
  const inputs = tableRow?.getElementsByTagName("input");
  if (inputs && inputs.length > 0) {
    inputs[0].focus();
  }
}

function generateNewName(nameTemplate: string, values: ILocalizedRecord<ITranslationTableTypeModel>[]) {
  if (values.every((x) => x[primaryLanguage.value.languageCode].key !== nameTemplate)) {
    return nameTemplate;
  }

  const pattern = new RegExp(nameTemplate + " \\((?<counter>\\d+)\\)");
  const counters = values
    .map((x) => pattern.exec(x[primaryLanguage.value.languageCode].key)?.groups?.counter)
    .filter((x) => x)
    .map((x) => parseInt(x))
    .sort((a, b) => a - b);
  const nextCounter = counters.length ? counters[counters.length - 1] + 1 : 1;
  return `${nameTemplate} (${nextCounter})`;
}

function syncAcrossLanguages(source: ILocalizedRecord<ITranslationTableTypeModel>, value: string) {
  if (!translatedLanguageCodes.value || translatedLanguageCodes.value.length === 0) return;
  nextTick(() => {
    translatedLanguageCodes.value.forEach((x) => (source[x].key = value));
  });
}

const feedbacks: Record<string, any> = computed(() => {
  const rules = {
    key: [
      {
        rule: (entry: ITranslationTableTypeModel) => !entry.key || entry.key.length <= 256, //either empty or less than 256 chars
        msg: $t("validation.lengthExceeds", { length: 256, field: $t("translation.translationKey") }),
        severity: "error"
      },
      {
        rule: (entry: ITranslationTableTypeModel) => entry.key,
        msg: $t("validation.valueRequired", { field: $t("translation.translationKey") }),
        severity: "error"
      },
      {
        rule: (entry: ITranslationTableTypeModel, lang: string) => vm.translations.filter((t) => t[lang]?.key && t[lang]?.key === entry?.key)?.length <= 1, //empty or does not match any other same language entry
        msg: $t("validation.duplicateExists", { field: $t("translation.translationKey") }),
        severity: "error"
      }
    ],
    value: [
      {
        rule: (entry: ITranslationTableTypeModel) => !entry.value || entry.value.length <= 1024,
        msg: $t("validation.lengthExceeds", { length: 1024, field: $t("translation.translationKey") }),
        severity: "error"
      },
      {
        rule: (entry: ITranslationTableTypeModel, lang: string, row: ILocalizedRecord<ITranslationTableTypeModel>) =>
          languages.value.some((lang) => row[lang.languageCode]?.value !== "" && row[lang.languageCode]?.value !== null),
        msg: $t("validation.requiredOneValue", { field: $t("translation.translationKey") }),
        severity: "error"
      }
    ]
  };

  const feedbacks = vm.translations.map((row) => ruleEvaluator(row, rules, languages.value));
  return feedbacks;
});

const hasValidationErrors = computed(() => {
  if (
    feedbacks.value
      .flatMap((row: ILocalizedRecord<ITranslationTableTypeModel>) => languages.value.map((lang) => row[lang.languageCode]))
      .some((row: Record<string, string>) => Object.keys(row).some((field: string) => !row[field].isValid && row[field].type == "error"))
  )
    return true;

  return false;
});

watch(
  () => vm.translations,
  (nv, ov) => {
    vm.dirty = nv === ov;
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  const _component_b_btn = _resolveComponent("b-btn")!
  const _component_b_input = _resolveComponent("b-input")!
  const _component_b_button = _resolveComponent("b-button")!
  const _component_b_dropdown_item = _resolveComponent("b-dropdown-item")!
  const _component_b_dropdown = _resolveComponent("b-dropdown")!
  const _component_spinner = _resolveComponent("spinner")!
  const _component_b_overlay = _resolveComponent("b-overlay")!
  const _component_b_modal = _resolveComponent("b-modal")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createBlock(_component_b_modal, {
    "header-close-content": "",
    title: _unref($t)('translation.table'),
    modelValue: _showTranslationTable.value,
    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_showTranslationTable).value = $event)),
    onOk: _cache[5] || (_cache[5] = ($event: any) => (SaveTranslations())),
    size: "xl",
    onClose: closeModal,
    onCancel: closeModal,
    onShow: loadTranslationTable,
    ref: "modal",
    "no-close-on-backdrop": "",
    "no-close-on-esc": ""
  }, {
    "modal-footer": _withCtx(({ ok, cancel }) => [
      _createVNode(_component_b_btn, {
        onClick: ok,
        variant: "primary",
        disabled: hasValidationErrors.value || !vm.dirty
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref($t)("OK")), 1)
        ]),
        _: 2
      }, 1032, ["onClick", "disabled"]),
      _createVNode(_component_b_btn, { onClick: cancel }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref($t)("Cancel")), 1)
        ]),
        _: 2
      }, 1032, ["onClick"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_b_btn, {
              class: "btn-with-icon mono",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (addNew())),
              disabled: hasValidationErrors.value
            }, {
              default: _withCtx(() => [
                _hoisted_4,
                _createTextVNode(_toDisplayString(_unref($t)("add")), 1)
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", {
                class: _normalizeClass(["input-group-prepend", { isFocused: vm.inputIsFocused }])
              }, _hoisted_8, 2),
              _createVNode(_component_b_input, {
                onFocus: _cache[1] || (_cache[1] = ($event: any) => (vm.inputIsFocused = true)),
                onBlur: _cache[2] || (_cache[2] = ($event: any) => (vm.inputIsFocused = false)),
                placeholder: _unref($t)('Filter'),
                modelValue: vm.searchTerm,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((vm.searchTerm) = $event))
              }, null, 8, ["placeholder", "modelValue"]),
              (vm.searchTerm)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(["input-group-append", { isFocused: vm.inputIsFocused }])
                  }, [
                    _createVNode(_component_b_button, { onClick: emptySearchTerm }, {
                      default: _withCtx(() => [
                        _hoisted_9
                      ]),
                      _: 1
                    })
                  ], 2))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createElementVNode("table", _hoisted_11, [
            _createElementVNode("thead", null, [
              _createElementVNode("tr", _hoisted_12, [
                _hoisted_13,
                _createElementVNode("th", _hoisted_14, [
                  _createElementVNode("span", _hoisted_15, _toDisplayString(_unref($t)("translation.translationKey")), 1)
                ]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(languages.value, (lang) => {
                  return (_openBlock(), _createElementBlock("th", {
                    key: `${lang.languageCode}-value`
                  }, [
                    _createElementVNode("span", null, _toDisplayString(_unref($t)("translation.translation")), 1),
                    (languages.value.length > 1)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_16, " (" + _toDisplayString(lang.languageCode) + ")", 1))
                      : _createCommentVNode("", true)
                  ]))
                }), 128))
              ])
            ]),
            _createElementVNode("tbody", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedList.value, (item, index) => {
                return (_openBlock(), _createElementBlock("tr", {
                  ref_for: true,
                  ref_key: "tableTranslation",
                  ref: tableTranslation,
                  key: index,
                  class: _normalizeClass({ selected: index === vm.selectedRow }),
                  onClick: ($event: any) => (selectRow(index))
                }, [
                  _createElementVNode("td", _hoisted_18, [
                    _createVNode(_component_b_dropdown, {
                      "no-caret": "",
                      variant: "primary",
                      class: "table-option",
                      "toggle-class": "popup-menu-button",
                      "menu-class": "popup-menu",
                      onToggle: ($event: any) => (selectRow(index))
                    }, {
                      "button-content": _withCtx(() => [
                        _hoisted_19
                      ]),
                      default: _withCtx(() => [
                        _createVNode(_component_b_dropdown_item, {
                          onClick: ($event: any) => (deleteItem(index)),
                          href: "#",
                          "link-class": "menu-option first-option"
                        }, {
                          default: _withCtx(() => [
                            _hoisted_20,
                            _createElementVNode("span", _hoisted_21, _toDisplayString(_unref($t)("contextMenu.delete")), 1)
                          ]),
                          _: 2
                        }, 1032, ["onClick"])
                      ]),
                      _: 2
                    }, 1032, ["onToggle"])
                  ]),
                  _createElementVNode("td", null, [
                    (item[primaryLanguage.value.languageCode])
                      ? _withDirectives((_openBlock(), _createElementBlock("input", {
                          key: 0,
                          type: "text",
                          class: _normalizeClass(["form-control", {
                    'border-danger':
                      feedbacks.value[index][primaryLanguage.value.languageCode].key &&
                      !feedbacks.value[index][primaryLanguage.value.languageCode].key.isValid &&
                      feedbacks.value[index][primaryLanguage.value.languageCode].key.type == 'error'
                  }]),
                          "onUpdate:modelValue": ($event: any) => ((item[primaryLanguage.value.languageCode].key) = $event),
                          onInput: ($event: any) => (syncAcrossLanguages(item, item[primaryLanguage.value.languageCode].key)),
                          maxlength: 256
                        }, null, 42, _hoisted_22)), [
                          [_vModelText, item[primaryLanguage.value.languageCode].key],
                          [_directive_tooltip, 
                    feedbacks.value[index][primaryLanguage.value.languageCode].key &&
                    !feedbacks.value[index][primaryLanguage.value.languageCode].key.isValid &&
                    feedbacks.value[index][primaryLanguage.value.languageCode].key.feedback
                  ]
                        ])
                      : _createCommentVNode("", true)
                  ]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(languages.value, (lang) => {
                    return (_openBlock(), _createElementBlock("td", {
                      key: `${lang.languageCode}-${index}-value`
                    }, [
                      (item && item[lang.languageCode] != null)
                        ? (_openBlock(), _createBlock(TextBoxWithFallback, {
                            key: 0,
                            maxLength: 1024,
                            source: item,
                            field: "value",
                            modelValue: item[lang.languageCode].value,
                            "onUpdate:modelValue": ($event: any) => ((item[lang.languageCode].value) = $event),
                            language: lang,
                            "validation-data": feedbacks.value[index][lang.languageCode].value
                          }, null, 8, ["source", "modelValue", "onUpdate:modelValue", "language", "validation-data"]))
                        : _createCommentVNode("", true)
                    ]))
                  }), 128))
                ], 10, _hoisted_17))
              }), 128))
            ])
          ])
        ]),
        _createVNode(_component_b_overlay, {
          "z-index": "20",
          opacity: "0.4",
          "no-wrap": "",
          show: vm.loading
        }, {
          overlay: _withCtx(() => [
            _createVNode(_component_spinner, { "data-cy": "manage-catalog-modal-spinner" })
          ]),
          _: 1
        }, 8, ["show"])
      ])
    ]),
    _: 1
  }, 8, ["title", "modelValue"]))
}
}

})